import { isDesktop } from '../utils/deviceType';
import LandingPage from '../views/landing-page.vue';

/**
 * userAgentData will be the prefered method to capture device information.
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgentData#browser_compatibility| userAgentData compatibility}
 */
/** @type {?boolean} */
const isMobileDevice = navigator.userAgentData?.mobile ?? null;
/**
 *
 * @description
 * Consider using {@link https://router.vuejs.org/guide/advanced/navigation-failures.html| Mobile Nav Menu State}
 * If new catch all routes are necessary prefer using ':pathMatch(.*)\*' in the path key.
 * {@link https://router.vuejs.org/guide/migration/#removal-of-the-fallback-option| decprecate catch-all}
 */
export default [
  {
    path: '/',
    redirect: { name: 'disclaimer' },
  },
  {
    path: '/forward',
    redirect:
    (from) => ({ name: 'disclaimer', query: { ...from.query, referral: 'forward' } }),
  },
  {
    path: '/mg',
    redirect:
    (from) => ({ name: 'disclaimer', query: { ...from.query, referral: 'mg' } }),
  },
  {
    path: '/:pathMatch(m|mg|mgo)',
    redirect: { name: 'disclaimer' },
  },
  {
    path: '/r/:encodedOrderUuid',
    name: 'sms-return-to-site',
    meta: { isMobileDevice, pageName: 'SMS Return To Site', noindex: true },
    component: () => import(/* webpackChunkName: 'smsRts' */ '../views/SmsReturnToSite.vue'),
  },
  {
    path: '/simulator/:orderUuid',
    name: 'simulator',
    meta: { isMobileDevice, pageName: 'Simulator', noindex: true },
    component: () => import(/* webpackChunkName: 'simulator' */ '../views/Simulator.vue'),
  },
  {
    path: '/action',
    name: 'action',
    component: () => import(/* webpackChunkName: 'post' */ '../views/Action.vue'),
  },
  {
    path: '/mgo/verification-success',
    name: 'verification-success',
    meta: { isMobileDevice, pageName: 'Two Factor Link Landing Page', noindex: true },
    component: () => import('../views/TwoFactor/verification-success.vue'),
  },
  {
    path: '/mgo/verification-failure',
    name: 'verification-failure',
    meta: { isMobileDevice, pageName: 'Two Factor Link Verification Failure', noindex: true },
    component: () => import('../views/TwoFactor/verification-failure.vue'),
  },
  {
    path: '/mgo/smfa/:coaUUID',
    name: 'verification-success',
    meta: { isMobileDevice, pageName: 'SMFA Redirect Landing Page', noindex: true },
    component: () => import('../views/TwoFactor/verification-success.vue'),
  },
  {
    path: '/mgo',
    name: 'landing-page',
    component: LandingPage,
    children: [
      {
        path: 'address-info',
        name: 'address-info',
        meta: {
          isMobileDevice, pageName: 'Address Info', currentStep: 1, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'address' */ '../views/AddressInfo/desktop.vue')
          : import(/* webpackChunkName: 'address' */ '../views/AddressInfo/mobile.vue')),
      },
      {
        path: 'contact-info',
        name: 'contact-info',
        meta: {
          isMobileDevice, pageName: 'Contact Info', currentStep: 2, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'contact' */ '../views/ContactInfo/desktop.vue')
          : import(/* webpackChunkName: 'contact' */ '../views/ContactInfo/mobile.vue')),
      },
      {
        path: 'military-confirmation/:coaUUID',
        name: 'military-confirm',
        meta: { isMobileDevice, pageName: 'Military Confirm', noindex: true },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'military' */ '../views/MilitaryConfirmation/desktop.vue')
          : import(/* webpackChunkName: 'military' */ '../views/MilitaryConfirmation/desktop.vue')),
      },
      {
        path: 'informed-delivery-signup/:coaUUID',
        name: 'informed-delivery-signup',
        meta: { isMobileDevice, pageName: 'Informed Delivery Signup', noindex: true },
        component: () => import(/* webpackChunkName: 'email' */ '../views/InformedDeliverySignup.vue'),
      },
      {
        path: 'additional-services',
        name: 'additional-services',
        meta: {
          isMobileDevice, pageName: 'Additional Services', currentStep: 3, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'desktop' */ '../choice-views/AdditionalServices/Desktop.vue')
          : import(/* webpackChunkName: 'mobile'  */ '../choice-views/AdditionalServices/Mobile.vue')),
      },
      {
        path: 'temporary-moves',
        name: 'temporary-moves',
        meta: { isMobileDevice, pageName: 'Temporary Moves', noindex: true },
        component: () => import(/* webpackChunkName: 'static' */ '../views/TemporaryMoves.vue'),
      },
      {
        path: 'types-of-forwarding',
        name: 'types-of-forwarding',
        meta: { isMobileDevice, pageName: 'Types of Forwarding', noindex: true },
        component: () => import(/* webpackChunkName: 'static' */ '../views/TypesOfForwarding.vue'),
      },
      {
        path: 'mail-forwarding-instructions',
        name: 'mail-forwarding-instructions',
        meta: { isMobileDevice, pageName: 'Mail Forwarding Instructions', noindex: true },
        component: () => import(/* webpackChunkName: 'static' */ '../views/MailForwardingInstructions.vue'),
      },
      {
        path: 'when-to-file',
        name: 'when-to-file',
        meta: { isMobileDevice, pageName: 'When To File', noindex: true },
        component: () => import(/* webpackChunkName: 'static' */ '../views/WhenToFile.vue'),
      },
      {
        path: 'terms-of-use',
        name: 'terms-of-use',
        meta: { isMobileDevice, pageName: 'Terms of Use', noindex: true },
        component: () => import(/* webpackChunkName: 'static' */ '../views/TermsOfUse.vue'),
      },
      {
        path: 'unsubscribe/:orderUuid',
        name: 'email-unsubscribe',
        meta: { isMobileDevice, pageName: 'Email Unsubscribe', noindex: true },
        component: () => import(/* webpackChunkName: 'email' */ '../views/EmailUnsubscribe.vue'),
      },
      {
        path: 'unsubscribe',
        name: 'email-unsubscribe',
        meta: { isMobileDevice, pageName: 'Email Unsubscribe', noindex: true },
        component: () => import(/* webpackChunkName: 'email' */ '../views/EmailUnsubscribe.vue'),
      },
      {
        path: 'disclaimer',
        name: 'disclaimer',
        meta: { isMobileDevice, pageName: 'Disclaimer' },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'bootstrap' */ '../splits/Disclaimer/MMChoiceDesktop.vue')
          : import(/* webpackChunkName: 'bootstrap' */ '../splits/Disclaimer/MMChoiceMobile.vue')),
      },
      {
        path: 'move-combined',
        name: 'move-combined',
        meta: { isMobileDevice, pageName: 'Move Combined', noindex: true },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'desktop' */ '../views/MoveCombined/desktop.vue')
          : import(/* webpackChunkName: 'mobile' */ '../views/MoveCombined/mobile.vue')),
      },
      {
        path: 'move-combined-mobile',
        name: 'move-combined-mobile',
        meta: { meta: { isMobileDevice, pageName: 'Move Combined', noindex: true } },
        component: () => import(/* webpackChunkName: 'mobile' */ '../views/MoveCombined/mobile-skip.vue'),
      },
      {
        path: 'choice-move-combined',
        name: 'choice-move-combined',
        meta: { isMobileDevice, pageName: 'Choice Move Combined', noindex: true },
        component: () => import(/* webpackChunkName: 'mobile' */ '../choice-views/MoveCombined/mobile.vue'),
      },
      {
        path: 'verify-info',
        component: () => import(/* webpackChunkName: 'bootstrap' */ '../views/VerifyInfo/index.vue'),
        children: [
          {
            path: '',
            meta: {
              isMobileDevice, pageName: 'Verify Information', currentStep: 4, noindex: true,
            },
            name: 'verify-info-read',
            component: () => (isDesktop()
              ? import(/* webpackChunkName: 'desktop' */ '../views/VerifyInfoRead/desktop.vue')
              : import(/* webpackChunkName: 'mobile' */ '../views/VerifyInfoRead/mobile.vue')),
          },
          {
            path: 'edit',
            meta: {
              isMobileDevice, pageName: 'Verify Information', currentStep: 4, noindex: true,
            },
            name: 'verify-info-edit',
            component: () => (isDesktop()
              ? import(/* webpackChunkName: 'desktop' */ '../views/VerifyInfoEdit/desktop.vue')
              : import(/* webpackChunkName: 'mobile' */ '../views/VerifyInfoEdit/mobile.vue')),
          },
          {
            path: 'edit/:orderId',
            meta: {
              isMobileDevice, pageName: 'Verify Information', currentStep: 4, noindex: true,
            },
            name: 'verify-info-edit-orderId',
            component: () => (isDesktop()
              ? import(/* webpackChunkName: 'desktop' */ '../views/VerifyInfoEditMultipleBusiness/desktop.vue')
              : import(/* webpackChunkName: 'mobile' */ '../views/VerifyInfoEditMultipleBusiness/mobile.vue')),
          },
        ],
      },
      {
        path: 'age-verification',
        name: 'age-verification',
        meta: { isMobileDevice, pageName: 'Age Verification', noindex: true },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'dcoa' */ '../views/AgeVerification/desktop.vue')
          : import(/* webpackChunkName: 'dcoa' */ '../views/AgeVerification/mobile.vue')),
      },
      {
        path: 'identity-check',
        name: 'identity-check',
        meta: {
          isMobileDevice, pageName: 'Identity Check', currentStep: 4, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'desktop' */ '../views/IdentityCheck/desktop.vue')
          // TODO: Bake in Champion on conclusion of Test
          // https://imagitas.atlassian.net/browse/MGOD-4419
          : import(/* webpackChunkName: 'mobile' */ '../splits/IdentityCheck/NoModalProvider.vue')),
      },
      {
        path: 'instant-savings/:orderUuid',
        name: 'instant-savings',
        meta: {
          isMobileDevice, pageName: 'Instant Savings', currentStep: 5, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'desktop' */ '../views/InstantSavings/desktop.vue')
          : import(/* webpackChunkName: 'mobile' */ '../views/InstantSavings/mobile.vue')),
      },
      {
        path: 'offer-details/:orderUuid',
        name: 'offer-details',
        meta: {
          isMobileDevice, pageName: 'Offer Details', currentStep: 5, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'rts' */ '../views/OfferDetails/desktop.vue')
          : import(/* webpackChunkName: 'rts' */ '../views/OfferDetails/mobile.vue')),
      },
      {
        path: 'error/processing-error',
        name: 'processing-error',
        meta: { isMobileDevice, pageName: 'Processing Error', noindex: true },
        component: () => import(/* webpackChunkName: 'error' */ '../views/ErrorProcessingError.vue'),
      },
      {
        path: 'error/residential-business',
        name: 'residential-business',
        meta: { isMobileDevice, pageName: 'Residential Business', noindex: true },
        component: () => import(/* webpackChunkName: 'error' */ '../views/ErrorResidentialBusiness.vue'),
      },
      {
        path: 'error/fatal-error',
        name: 'fatal-error',
        meta: { isMobileDevice, pageName: 'Fatal Error', noindex: true },
        component: () => import(/* webpackChunkName: 'error' */ '../views/ErrorFatalError.vue'),
      },
      {
        path: 'error/cmra',
        name: 'cmra',
        meta: { isMobileDevice, pageName: 'CMRA', noindex: true },
        component: () => import(/* webpackChunkName: 'error' */ '../views/ErrorCMRA.vue'),
      },
      {
        path: 'error/old-unique-zip',
        name: 'old-unique-zip',
        meta: { isMobileDevice, pageName: 'Old Unique Zip', noindex: true },
        component: () => import(/* webpackChunkName: 'error' */ '../views/ErrorOldUniqueZip.vue'),
      },
      {
        path: 'confirm-order/:orderUuid',
        name: 'confirm-order',
        meta: {
          isMobileDevice, pageName: 'Confirm Order', currentStep: 5, noindex: true,
        },
        component: () => (isDesktop()
          ? import(/* webpackChunkName: 'desktop' */ '../views/ConfirmOrder/desktop.vue')
          : import(/* webpackChunkName: 'mobile' */ '../views/ConfirmOrder/mobile.vue')),
      },
    ],
  },
  {
    path: '*',
    redirect: { name: 'disclaimer' },
  },
];
